const DEBUG_URLS_KEY = "tower3_debug_urls";

export interface SavedDebugUrl {
  id: string;
  url: string;
  timestamp: Date;
  label?: string;
}

export class DebugUrlStorage {
  private static isTestEnvironment(): boolean {
    return (
      typeof window !== "undefined" &&
      (window.location.href.includes("test") ||
        window.location.href.includes("playwright") ||
        process.env.NODE_ENV === "test")
    );
  }

  static getSavedUrls(): SavedDebugUrl[] {
    if (this.isTestEnvironment()) {
      return [];
    }

    try {
      const stored = localStorage.getItem(DEBUG_URLS_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((item: SavedDebugUrl & { timestamp: string }) => ({
        ...item,
        timestamp: new Date(item.timestamp),
      }));
    } catch (error) {
      console.warn("Failed to load saved debug URLs:", error);
      return [];
    }
  }

  static saveUrl(url: string, label?: string): void {
    if (this.isTestEnvironment()) {
      return;
    }

    try {
      const savedUrls = this.getSavedUrls();
      const newUrl: SavedDebugUrl = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        url,
        timestamp: new Date(),
        label,
      };

      savedUrls.unshift(newUrl); // Add to beginning of array
      
      // Keep only the last 10 URLs to prevent localStorage bloat
      const trimmedUrls = savedUrls.slice(0, 10);

      localStorage.setItem(DEBUG_URLS_KEY, JSON.stringify(trimmedUrls));
    } catch (error) {
      console.warn("Failed to save debug URL:", error);
    }
  }

  static updateLabel(id: string, newLabel: string): void {
    if (this.isTestEnvironment()) {
      return;
    }

    try {
      const savedUrls = this.getSavedUrls();
      const updatedUrls = savedUrls.map(item => 
        item.id === id ? { ...item, label: newLabel } : item
      );
      localStorage.setItem(DEBUG_URLS_KEY, JSON.stringify(updatedUrls));
    } catch (error) {
      console.warn("Failed to update debug URL label:", error);
    }
  }

  static deleteUrl(id: string): void {
    if (this.isTestEnvironment()) {
      return;
    }

    try {
      const savedUrls = this.getSavedUrls();
      const filteredUrls = savedUrls.filter(item => item.id !== id);
      localStorage.setItem(DEBUG_URLS_KEY, JSON.stringify(filteredUrls));
    } catch (error) {
      console.warn("Failed to delete debug URL:", error);
    }
  }

  static clearAllUrls(): void {
    if (this.isTestEnvironment()) {
      return;
    }

    try {
      localStorage.removeItem(DEBUG_URLS_KEY);
    } catch (error) {
      console.warn("Failed to clear debug URLs:", error);
    }
  }
}